import React, { useCallback, useMemo } from 'react';

import { DiagnosticContributorBoundary } from 'features/diagnostics/components/diagnostic-contributor-boundary';
import { diagnosticMoney } from 'features/diagnostics/context/projectors';
import {
  useDiagnosticContributor,
  useDiagnosticToolsEnabled,
} from 'features/diagnostics/context/provider';
import { DiagnosticPriceContribution } from 'features/diagnostics/context/types';
import { getModifierMultiplierPrice } from 'features/menu/components/product-detail/modifier/utils';
import { useProductWizardContext } from 'features/menu/components/product-wizard';
import { DEFAULT_SLOT_KEY } from 'features/menu/components/product-wizard/types';
import { useProductTotalPrice } from 'features/menu/components/product-wizard/use-product-total-price';
import { useProductWizardFlow } from 'features/menu/components/product-wizard/use-product-wizard-flow';

import { useLocale } from 'state/intl';
import { useUIContext } from 'state/ui';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import { getComboSlotItemPrice, isItem } from 'state/unified-menu/utils';

const localizedName = (entry: unknown): string | undefined => {
  const source = entry as { name?: { locale?: string } | string } | null;
  return typeof source?.name === 'string' ? source.name : source?.name?.locale;
};

const readPlu = (entry: unknown): string | undefined => {
  const source = entry as { externalReferencesId?: string; plu?: string } | null;
  return source?.externalReferencesId ?? source?.plu;
};

const readOptionalPrice = (readPrice: () => number | undefined): number | undefined => {
  try {
    return readPrice();
  } catch {
    return undefined;
  }
};

export const OneMenuDiagnosticPdpContributor = () => {
  const enabled = useDiagnosticToolsEnabled();

  return enabled ? (
    <DiagnosticContributorBoundary>
      <EnabledOneMenuDiagnosticPdpContributor />
    </DiagnosticContributorBoundary>
  ) : null;
};

const EnabledOneMenuDiagnosticPdpContributor = () => {
  const {
    menuObject,
    pickerSelections,
    productQuantity,
    selectedProduct,
    slotsSelections,
  } = useProductWizardContext();
  const flow = useProductWizardFlow();
  const { posServiceMode } = useMenuQueryOptions();
  const { indexedMenu } = useUnifiedMenuContext();
  const { formatCurrencyForLocale } = useUIContext();
  const { region } = useLocale();
  const currency = String(region).toUpperCase() === 'CA' ? 'CAD' : 'USD';
  const { totalPrice, formattedTotalPrice } = useProductTotalPrice({
    id: flow.id,
    selectedProduct,
    slotsSelections,
    productQuantity,
    cartIdEditing: flow.cartIdEditing,
    rewardId: flow.rewardId,
    reward: flow.reward,
  });
  const baseUnitAmount = selectedProduct.price?.[posServiceMode];
  const configuredUnitAmount = productQuantity > 0 ? totalPrice / productQuantity : totalPrice;

  const projectSelections = useCallback((): DiagnosticPriceContribution[] => {
    const projected: DiagnosticPriceContribution[] = Object.entries(pickerSelections).map(
      ([id, selectedId]) => ({
        kind: 'picker',
        id,
        sanityId: selectedId,
        name: localizedName(indexedMenu.find(selectedId)) ?? selectedId,
      })
    );
    for (const [slotId, slotSelection] of Object.entries(slotsSelections)) {
      if (!slotSelection) {
        continue;
      }
      const isSyntheticItemSlot = isItem(selectedProduct) && slotId === DEFAULT_SLOT_KEY;
      const selectedItem = isSyntheticItemSlot
        ? selectedProduct
        : indexedMenu.find(slotSelection.itemId);

      // Item PDPs store modifiers under a synthetic slot_0 key. It is not a COMBO_SLOT and must
      // never be passed to the strict combo-slot pricing lookup.
      if (!isSyntheticItemSlot) {
        const slotContribution = readOptionalPrice(
          () =>
            getComboSlotItemPrice(indexedMenu, slotId, slotSelection.itemId)?.[posServiceMode] ??
            undefined
        );
        projected.push({
          kind: 'slot',
          id: slotId,
          sanityId: slotSelection.itemId,
          name: localizedName(selectedItem) ?? slotSelection.itemId,
          plu: readPlu(selectedItem),
          quantity: 1,
          price:
            typeof slotContribution === 'number'
              ? diagnosticMoney({
                  minorAmount: slotContribution,
                  currency,
                  formatted: formatCurrencyForLocale(slotContribution),
                  source: 'menu_calculation',
                  availability: 'available',
                })
              : undefined,
        });
      }
      for (const [modifierId, multipliers] of Object.entries(slotSelection.modifiers ?? {})) {
        const modifier = indexedMenu.find(modifierId);
        if (!modifier) {
          continue;
        }
        for (const [multiplierId, multiplier] of Object.entries(multipliers)) {
          if (!multiplier) {
            continue;
          }
          const modifierContribution = readOptionalPrice(
            () => getModifierMultiplierPrice(modifier, multiplierId, posServiceMode) * multiplier
          );
          projected.push({
            kind: 'modifier',
            id: modifierId,
            sanityId: multiplierId,
            name: localizedName(modifier) ?? modifierId,
            plu: readPlu(modifier),
            multiplier,
            price:
              typeof modifierContribution === 'number'
                ? diagnosticMoney({
                    minorAmount: modifierContribution,
                    currency,
                    formatted: formatCurrencyForLocale(modifierContribution),
                    source: 'menu_calculation',
                    availability: 'available',
                  })
                : undefined,
          });
        }
      }
    }
    return projected;
  }, [
    currency,
    formatCurrencyForLocale,
    indexedMenu,
    pickerSelections,
    posServiceMode,
    selectedProduct,
    slotsSelections,
  ]);

  const getter = useMemo(
    () => () => ({
      pdp: {
        implementation: 'one_menu' as const,
        menuObjectType: String(menuObject.type ?? ''),
        rootId: menuObject.id,
        selectedId: selectedProduct.id,
        sanityId: selectedProduct.id,
        name: localizedName(selectedProduct),
        plu: readPlu(selectedProduct),
        quantity: productQuantity,
        step: flow.currentFlowStep.step,
        mode: flow.cartIdEditing ? ('edit' as const) : ('add' as const),
        cartEntryId: flow.cartIdEditing || undefined,
        offerId: flow.offerId,
        rewardId: flow.rewardId,
        rewardBenefitId: flow.rewardBenefitId,
        baseUnitPrice:
          typeof baseUnitAmount === 'number'
            ? diagnosticMoney({
                minorAmount: baseUnitAmount,
                currency,
                formatted: formatCurrencyForLocale(baseUnitAmount),
                source: 'menu_calculation',
                availability: 'available',
              })
            : diagnosticMoney({ source: 'menu_calculation', availability: 'unavailable' }),
        configuredUnitPrice: diagnosticMoney({
          minorAmount: configuredUnitAmount,
          currency,
          formatted: formatCurrencyForLocale(configuredUnitAmount),
          source: 'menu_calculation',
          availability: 'available',
        }),
        displayedTotal: diagnosticMoney({
          minorAmount: totalPrice,
          currency,
          formatted: formattedTotalPrice,
          source: 'displayed_ui',
          availability: formattedTotalPrice ? 'available' : 'unavailable',
        }),
        selections: projectSelections(),
      },
    }),
    [
      baseUnitAmount,
      configuredUnitAmount,
      currency,
      flow.cartIdEditing,
      flow.currentFlowStep.step,
      flow.offerId,
      flow.rewardBenefitId,
      flow.rewardId,
      formatCurrencyForLocale,
      formattedTotalPrice,
      menuObject.id,
      menuObject.type,
      productQuantity,
      selectedProduct,
      projectSelections,
      totalPrice,
    ]
  );
  useDiagnosticContributor('pdp-one-menu', getter);

  return null;
};
